/**
 * DEC5 reads (RLS) and writes (RPCs only). A failed read THROWS: the page must
 * never show "no plan" for "could not read". RPC error messages are matched
 * verbatim against migration 118 (and the 22023 messages added in its fix round).
 *
 * @module services/targets/targetsService
 */
import { getSupabaseClient } from '@/config';
import { logError } from '@/services/logger';
import { readAllRows } from '@/services/supabasePaging';
import { isAwarenessMetric, type AwarenessMetric, type ResultMappingRow, type ResultMetric } from './resultMetric';
import type { CprTargetRow, PlanRow, ProposalStatus, TargetObjective, TargetPlatform } from './targetRules';

export type TargetErrorCode = 'notAllowed' | 'monthClosed' | 'stale' | 'alreadyDecided' | 'effectivePassed'
  | 'reasonRequired' | 'notYours' | 'monthNotClosed' | 'invalidInput' | 'unknown';

const CODES = new Map<string, TargetErrorCode>(Object.entries({
  'not allowed': 'notAllowed', 'month closed': 'monthClosed', stale: 'stale', 'already decided': 'alreadyDecided',
  'effective date passed': 'effectivePassed', 'reason required': 'reasonRequired', 'not your proposal': 'notYours',
  'month not closed': 'monthNotClosed', 'invalid month': 'invalidInput', 'invalid date': 'invalidInput', 'invalid mapping': 'invalidInput',
} as Record<string, TargetErrorCode>));

export class TargetWriteError extends Error {
  readonly code: TargetErrorCode;
  constructor(message: string) {
    super(message);
    this.name = 'TargetWriteError';
    this.code = CODES.get(message.trim()) ?? 'unknown';
  }
}

export interface WorkspaceTargets { plans: PlanRow[]; targets: CprTargetRow[] }

interface DecisionDb {
  status: string; proposed_by: string; proposed_at: string; decided_by: string | null; decided_at: string | null;
  reject_reason: string | null; override_reason: string | null;
}
interface PlanDb extends DecisionDb { id: string; workspace_id: string; month: string; currency: string; platform: string | null; amount: number | string }
interface TargetDb extends DecisionDb {
  id: string; workspace_id: string; objective: string; metric: string; currency: string; platform: string | null; target: number | string; effective_from: string;
}

const decisionOf = (r: DecisionDb) => ({
  status: r.status as ProposalStatus, proposedBy: r.proposed_by, proposedAt: r.proposed_at, decidedBy: r.decided_by,
  decidedAt: r.decided_at, rejectReason: r.reject_reason, overrideReason: r.override_reason,
});
const planOf = (r: PlanDb): PlanRow => ({
  ...decisionOf(r), id: r.id, workspaceId: r.workspace_id, month: r.month, currency: r.currency.trim(),
  platform: r.platform as TargetPlatform | null, amount: Number(r.amount),
});
const targetOf = (r: TargetDb): CprTargetRow => ({
  ...decisionOf(r), id: r.id, workspaceId: r.workspace_id, objective: r.objective as TargetObjective, metric: r.metric as ResultMetric, currency: r.currency.trim(),
  platform: r.platform as TargetPlatform | null, target: Number(r.target), effectiveFrom: r.effective_from,
});

type ReadTable = 'workspace_budget_plans' | 'workspace_cpr_targets' | 'workspace_result_mappings';
const readTable = async <T>(table: ReadTable, workspaceId: string, newestFirst: 'proposed_at' | 'set_at' = 'proposed_at'): Promise<T[]> => {
  const { rows, error } = await readAllRows<T>(
    (lo, hi) => getSupabaseClient().from(table).select('*').eq('workspace_id', workspaceId)
      .order(newestFirst, { ascending: false }).order('id', { ascending: true }).range(lo, hi) as unknown as PromiseLike<{ data: T[] | null; error: unknown }>,
    table,
  );
  if (error) {
    const msg = (error as { message?: string } | null)?.message ?? String(error);
    logError('targetsService', `${table} read failed`, error instanceof Error ? error : new Error(msg));
    throw new Error(`${table} read failed: ${msg}`);
  }
  return rows;
};

export const fetchWorkspaceTargets = async (workspaceId: string): Promise<WorkspaceTargets> => {
  const [plans, targets] = await Promise.all([
    readTable<PlanDb>('workspace_budget_plans', workspaceId),
    readTable<TargetDb>('workspace_cpr_targets', workspaceId),
  ]);
  return { plans: plans.map(planOf), targets: targets.map(targetOf) };
};

const countProposed = async (table: 'workspace_budget_plans' | 'workspace_cpr_targets', workspaceId: string): Promise<number> => {
  const { count, error } = await getSupabaseClient().from(table).select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId).eq('status', 'proposed');
  if (error) throw new Error(`${table} count failed`);
  if (count === null) throw new Error(`${table} count missing`);
  return count;
};

export const fetchPendingCount = async (workspaceId: string): Promise<number> => {
  const [a, b] = await Promise.all([countProposed('workspace_budget_plans', workspaceId), countProposed('workspace_cpr_targets', workspaceId)]);
  return a + b;
};

/** Unwraps an RPC reply: an error becomes a TargetWriteError carrying its code. */
const settle = <T>(res: { data: T; error: { message?: string } | null }): T => {
  if (res.error) throw new TargetWriteError(res.error.message ?? 'unknown');
  return res.data;
};

/** A propose/override RPC must hand back the row it wrote; null is a failure, not a result. */
const settleRow = <T>(res: { data: T | null; error: { message?: string } | null }): T => {
  const data = settle(res);
  if (data === null) throw new TargetWriteError('unknown');
  return data;
};

export const proposePlan = async (a: { workspaceId: string; month: string; currency: string; platform: TargetPlatform | null; amount: number; expectedVersion: string | null }): Promise<PlanRow> =>
  planOf(settleRow(await getSupabaseClient().rpc('propose_budget_plan', {
    p_workspace_id: a.workspaceId, p_month: a.month, p_currency: a.currency, p_platform: a.platform, p_amount: a.amount,
    p_expected_version: a.expectedVersion,
  })));

export const proposeTarget = async (a: { workspaceId: string; objective: TargetObjective; currency: string; platform: TargetPlatform | null; target: number; effectiveFrom: string; expectedVersion: string | null; reason?: string }): Promise<CprTargetRow> =>
  targetOf(settleRow(await getSupabaseClient().rpc('propose_cpr_target', {
    p_workspace_id: a.workspaceId, p_objective: a.objective, p_currency: a.currency, p_platform: a.platform, p_target: a.target,
    p_effective_from: a.effectiveFrom, p_expected_version: a.expectedVersion, p_reason: a.reason ?? null,
  })));

export const withdrawProposal = async (kind: 'plan' | 'target', id: string): Promise<void> => {
  settle(await getSupabaseClient().rpc('withdraw_target_proposal', { p_kind: kind, p_id: id }));
};

export const decideProposal = async (kind: 'plan' | 'target', id: string, approve: boolean, reason?: string): Promise<void> => {
  settle(await getSupabaseClient().rpc('decide_target_proposal', { p_kind: kind, p_id: id, p_approve: approve, p_reason: reason ?? null }));
};

export const overrideLockedPlan = async (a: { workspaceId: string; month: string; currency: string; platform: TargetPlatform | null; amount: number; reason: string }): Promise<PlanRow> =>
  planOf(settleRow(await getSupabaseClient().rpc('override_locked_plan', {
    p_workspace_id: a.workspaceId, p_month: a.month, p_currency: a.currency, p_platform: a.platform, p_amount: a.amount, p_reason: a.reason,
  })));

interface MappingDb { id: string; workspace_id: string; objective: string; metric: string; status: string; set_by: string; set_at: string }

/** Fail closed: a row outside the allow-list is never read as a definition. */
const mappingOf = (r: MappingDb): ResultMappingRow => {
  if (r.objective !== 'awareness' || !isAwarenessMetric(r.metric) || (r.status !== 'active' && r.status !== 'superseded')) {
    throw new Error(`workspace_result_mappings: unexpected row ${r.objective}/${r.metric}/${r.status}`);
  }
  return { id: r.id, workspaceId: r.workspace_id, objective: 'awareness', metric: r.metric, status: r.status, setBy: r.set_by, setAt: r.set_at };
};

/** Every version of the workspace's mapping, newest first. Throws on failure: an unread mapping is not "Impressions". */
export const fetchResultMappings = async (workspaceId: string): Promise<ResultMappingRow[]> =>
  (await readTable<MappingDb>('workspace_result_mappings', workspaceId, 'set_at')).map(mappingOf);

/** The one write (migration 120). A same-value save is a no-op server-side; the caller reloads either way (R6). */
export const setResultMapping = async (a: { workspaceId: string; objective: 'awareness'; metric: AwarenessMetric }): Promise<void> => {
  settle(await getSupabaseClient().rpc('set_result_mapping', { p_workspace_id: a.workspaceId, p_objective: a.objective, p_metric: a.metric }));
};
